import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import BreadcrumbDetailed from "components/Common/BreadcrumbDetailed";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  userLogin,
  fetchCustomerForgotPasswordSuccess,
  clearLoginMessage,
} from "redux/actions/user";
import { useForm } from "react-hook-form";
import "./login-page.scss";
import { getLocalStorageValue } from "config/helper";
import { SHOP_FOR_CUSTOMER_PATH } from 'routes';
import { Clients } from 'config/constant';

const LoginPage = () => {
  const recaptchaRef = React.createRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? "";
  
  useEffect(() => {
    if (email) {
      reset({email});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const proxyShopping = location.pathname === SHOP_FOR_CUSTOMER_PATH;

  const clientId = getLocalStorageValue("RSAclient-id");
  const isRedners = clientId === Clients.Redners;

  const [recaptchaValue, setRecaptchaValue] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const {loginError, loginLoading} = useSelector(({ user }) => user);

  const headerSectionData =
    useSelector(({ common }) => common.commonDetailsData.header_section) || {};

  const {
    your_recaptcha_site_key,
    numeric_password_length,
  } = headerSectionData;
  
  let { 
    complex_password_min_length, 
    complex_password_max_length, 
    is_numeric_password 
  } = headerSectionData;
  if (isRedners) {
    complex_password_min_length = 4
    complex_password_max_length = 30
    is_numeric_password = "1"
  }

  const { register, handleSubmit, errors, reset } = useForm();

  useEffect(() => {
    dispatch(fetchCustomerForgotPasswordSuccess(""));
  }, [dispatch]);

  const handleLoginSuccess = () => {
    const redirectUrl = sessionStorage.getItem("_aog_login_redirect_url")
    if (redirectUrl) { 
      sessionStorage.removeItem("_aog_login_redirect_url");
      // Add a small delay to ensure the auth state is updated
      setTimeout(() => {
        navigate(redirectUrl);
      }, 10);
    } else {
      navigate("/");
    }
  };

  const submitUserLogin = (data) => {
    const { email, password } = data;
    const body = {
      RSAClientId: clientId,
      email_id: email,
      password: password,
    };
    if (recaptchaValue) {
      setCaptchaError("");
      dispatch(userLogin(body, handleLoginSuccess, proxyShopping));
    } else {
      setCaptchaError("The captcha field is required");
    }
  };

  const onChangeReCaptcha = (value) => {
    setRecaptchaValue(value);
    setCaptchaError("");
  };

  const lable = proxyShopping ? "Proxy Sign In" : "Sign In";
  const breadcrumbList = [{ lable, link: "" }];

  useEffect(() => {
    return () => {
      dispatch(clearLoginMessage());
    };
  }, [dispatch]);

  let minLength, maxLength
  if (proxyShopping) {
    minLength = 1
    maxLength = 6
  } else if (isRedners) {
    minLength = 4
    maxLength = 30
  } else {
    minLength = is_numeric_password === "1" ? numeric_password_length : 4
    maxLength = is_numeric_password === "1" ? numeric_password_length : complex_password_max_length
  }

  function GetPasswordError() {
    const type = errors?.password?.type;
    if (!type) {
      return null
    }

    if (is_numeric_password) {
      if (type === "required") {
        return <span>Please enter Pin.</span>
      }

      if (type === "pattern") {
        return <span>Pin must contain only number.</span>
      }

      if (["minLength", "maxLength"].includes(type)) {
        if (isRedners) {
          return <span>PIN must be {minLength} - {maxLength} digits.</span>
        }
        return <span>Pin must only {minLength} digits.</span>
      }
    } else {
      if (type === "required") {
        return <span>Please enter Password.</span>
      }

      if (["minLength", "maxLength"].includes(type)) {
        return <span>Password must only {minLength} - {maxLength} characters.</span>
      }
    }

    return null
  }

  return (
    <>
      <BreadcrumbDetailed breadcrumbList={breadcrumbList} title={lable} />

      <div className="login-page-wrapper">
        <div className="container">
          <div className="auth-grid-alignment">
            <div className="auth-grid">
              <div className="auth-grid-items">
                <div className="logo-center-wrapper">
                    <img
                      src={headerSectionData.rsa_client_logo}
                    alt={headerSectionData.image_alt_text}
                    />
                </div>
                <h1>
                  Great to
                  <span>see you back</span>
                </h1>
                 {proxyShopping ? null : <p>
                  Don't have an account?{" "}
                  <span>
                    <Link to="/signup" className="create-btn">Create here</Link>
                  </span>
                </p>}
              </div>
              <div className="auth-grid-items">
                <h2> {proxyShopping ? <span>Proxy</span> : ""} Sign In</h2>
                <form name="login" onSubmit={handleSubmit(submitUserLogin)} noValidate>
                  <div className="form-control">
                    <label>Email Address *</label>
                    <input
                      name="email"
                      id="email"
                      ref={register({
                        required: true,
                        pattern:
                          /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                      })}
                    />
                    {errors.email && errors.email.type === "required" && (
                      <span>Please enter email.</span>
                    )}
                    {errors.email && errors.email.type === "pattern" && (
                      <span>
                        The email address entered is invalid, please check the
                        formatting (e.g. email@domain.com)
                      </span>
                    )}
                  </div>
                  {is_numeric_password === "1" ? (
                    <div className="form-control">
                      <div className="pin-wrap">
                      <label> {proxyShopping ? "Enter the code*" : "Pin *"} </label>
                      {/* {proxyShopping ? <Link to="/" className="code-link">Send Code</Link> : null} */}
                      </div>
                      <input
                        name="password"
                        id="password"
                        type="password"
                        minLength={minLength}
                        maxLength={maxLength}
                        ref={register({
                          required: true,
                          minLength: minLength,
                          pattern: /^[0-9]+$/, // eslint-disable-line
                        })}
                      />

                      <GetPasswordError />
                      <div className="forgot-pin">
                          {proxyShopping ? null : <Link to="/forgot-password">Forgot Pin ?</Link>}
                      </div>
                    </div>
                  ) : (
                    <div className="form-control">
                      <label> Password *</label>
                      <input
                        name="password"
                        id="password"
                        type="password"
                        maxLength={maxLength}
                        minLength={minLength}
                        ref={register({
                          required: true,
                          minLength,
                          maxLength,
                        })}
                      />
                      <GetPasswordError />
                      <Link to="/forgot-password">Forgot Password?</Link>
                    </div>
                  )}
                  {loginError ? (
                    <span className="login-error">{loginError}</span>
                  ) : (
                    ""
                  )}
                    <div className="re-captcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={your_recaptcha_site_key}
                        onChange={onChangeReCaptcha}
                      />
                      <span style={{ color: "red" }}>{captchaError}</span>
                    </div>
                  <div className="sign-in-button">
                    <button type="submit" disabled={loginLoading}>
                      {loginLoading ? "Signing In..." : "Sign In"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
